import { X } from "lucide-react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Separator } from "@/common/ui/separator";

import type { GuardrailEvent } from "./guardrail-metrics-types";

interface EventDetailPanelProps {
  event: GuardrailEvent;
  onClose: () => void;
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

export function EventDetailPanel({ event, onClose }: EventDetailPanelProps): React.ReactElement {
  const intervention = event.guardrail_intervention;

  return (
    <Card data-testid="event-detail-panel" className="h-fit">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Intervention Details</CardTitle>
        <Button
          data-testid="close-detail-button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DetailField label="Timestamp" value={new Date(event.timestamp).toLocaleString()} />
          <DetailField label="Environment" value={event.environment} />
          <DetailField label="User ID" value={<span className="font-mono">{event.user_id}</span>} />
          <DetailField
            label="Session ID"
            value={<span className="font-mono text-xs">{event.session_id}</span>}
          />
          <DetailField label="Model" value={event.model_id} />
          <DetailField label="Audit Action" value={event.action} />
        </div>

        <Separator />

        <div>
          <span className="text-xs font-medium text-muted-foreground">Guardrail Intervention</span>
          <div
            data-testid="intervention-detail"
            className="mt-1 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs"
          >
            <div className="flex items-center gap-2">
              <Badge variant="destructive">{intervention.action}</Badge>
              <Badge variant={intervention.source === "input" ? "default" : "secondary"}>
                {intervention.source}
              </Badge>
            </div>

            {intervention.reason && (
              <p data-testid="intervention-reason" className="mt-2">
                {intervention.reason}
              </p>
            )}

            <div className="mt-2 flex flex-wrap gap-1">
              {intervention.policies.map((p) => (
                <Badge key={p} variant="outline" className="text-xs">
                  {p}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
